import { Menu } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { useAccountPersonType, useOnboardingGates } from '@tankef/core-react';
import { useTranslation, changeLanguage, getCurrentLanguage } from '@tankef/i18n';
import {
  UserIcon,
  BellIcon,
  ShieldIcon,
  BanknoteIcon,
  PaletteIcon,
  EyeIcon,
  LockIcon,
  QuestionMarkCircleIcon,
  CardFrontIcon,
  FileTextIcon,
} from '@tankef/icons';
import { AnalyticsEvents, useAnalytics } from '@tankef/telemetry';
import {
  Logo,
  IconButton,
  AccountMenu,
  type AccountMenuItem,
  NotificationPanel,
  SessionWarning,
  LanguageSelector,
  type LanguageOption,
} from '@tankef/ui';

import { AccountSwitcher } from './AccountSwitcher';
import { PageTitleArea } from './PageTitleArea';
import { useNotificationsContext } from '../notifications-context';

import { useClientLogout, useParty } from '@/domains/identity/auth/api';
import { onboardingTaskRoutes } from '@/domains/identity/onboarding/lib/onboardingTaskRoutes';
import { type Notification as RealtimeNotification } from '@/domains/notifications/api';
import { useInactivityTimeout, useSessionWarning } from '@/shared/session/api';
import { flowStorage } from '@/shared/session/recovery';

interface HeaderSectionProps {
  onMenuClick: () => void;
}

const languages: LanguageOption[] = [
  { code: 'es', label: 'Español', countryCode: 'MX' },
  { code: 'en', label: 'English', countryCode: 'US' },
];

function mapNotifications(notifications: RealtimeNotification[]) {
  return notifications.map(n => ({
    id: n.id,
    title: n.title,
    description: n.message,
    timestamp: new Date(n.created_at),
    isRead: n.read,
    type: n.type,
  }));
}

/**
 * Renders the right portion of the full-width header (title + user controls).
 * The logo section is handled by DashboardLayout so its width can be synced
 * with the sidebar collapsed/expanded state.
 */
export function HeaderSection({ onMenuClick }: HeaderSectionProps) {
  const { t } = useTranslation('common');
  const { track } = useAnalytics();
  const party = useParty();
  const logout = useClientLogout();
  const navigate = useNavigate();
  const location = useLocation();

  const activeSettingsSection = (): string | null => {
    if (location.pathname !== '/settings') return null;
    return new URLSearchParams(location.search).get('section') || 'profile';
  };
  const activeSection = activeSettingsSection();

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => {
        track(AnalyticsEvents.LOGOUT);
        // Drop any in-progress flow drafts (savings-box, kyc, etc.) so the
        // next user on this device doesn't see a stale "Resume" banner.
        flowStorage.clearAll();
        navigate('/login');
      },
    });
  };

  const { resetTimers } = useInactivityTimeout(handleLogout);
  const { showWarning, remainingTime, onExtend } = useSessionWarning(resetTimers);

  const handleLanguageChange = (languageCode: string) => {
    changeLanguage(languageCode as 'es' | 'en');
  };

  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotificationsContext();

  // Atajos a lo que le falta al expediente. El cintillo del dashboard ya
  // anuncia el siguiente paso, pero desaparece al navegar a cualquier otra
  // pantalla y el usuario se queda sin puerta de regreso; el menú de perfil
  // está en todas.
  //
  // Se listan las compuertas por separado, no `onboarding.next_action`: aquél
  // trae sólo la primera pendiente porque un cintillo debe decir UNA cosa. Un
  // menú puede mostrar las dos y dejar que el usuario elija por dónde entrar.
  // Las rutas NO son las del BE: su `next_action.href` apunta a
  // /onboarding/kyc y /onboarding/esign, que no existen en el router y
  // dejan al usuario en el shell vacío. Ver onboardingTaskRoutes.
  const { onboarding } = useOnboardingGates();
  const { isPm } = useAccountPersonType();
  const taskRoutes = onboardingTaskRoutes(isPm);

  const pendingOnboardingItems: AccountMenuItem[] = [];
  if (onboarding && !onboarding.complete) {
    if (!onboarding.kyc_verified) {
      pendingOnboardingItems.push({
        key: 'complete-kyc',
        label: t('toolbar.completeKyc'),
        icon: <CardFrontIcon className="h-4 w-4" />,
        href: taskRoutes.kyc,
        isActive: location.pathname.startsWith(taskRoutes.kyc),
      });
    }
    if (!onboarding.contracts_signed) {
      pendingOnboardingItems.push({
        key: 'sign-documents',
        label: t('toolbar.signDocuments'),
        icon: <FileTextIcon className="h-4 w-4" />,
        href: taskRoutes.contracts,
        isActive: location.pathname.startsWith(taskRoutes.contracts),
      });
    }
  }

  const user = {
    displayName: party?.fullName || 'Usuario',
    email: party?.email || '',
    initials: party?.initials,
  };

  return (
    <>
      {/* flex-1 so this section fills the space after the logo column */}
      <div className="flex flex-1 items-center justify-between px-4 lg:px-6">
        {/* Left: mobile hamburger + mobile logo / desktop page title */}
        <div className="flex items-center gap-2 lg:gap-3">
          <IconButton
            variant="ghost"
            size="md"
            onClick={onMenuClick}
            className="lg:hidden"
            aria-label={t('toolbar.openMenu')}
          >
            <Menu className="h-5 w-5" />
          </IconButton>

          {/* Logo shown only on mobile (desktop logo is in the sidebar-width column) */}
          <Link to="/dashboard" className="flex items-center lg:hidden">
            <Logo isSingle disableLink />
          </Link>

          {/* Desktop: page title from usePageHeader / route handle */}
          <PageTitleArea />
        </div>

        {/* Right: account switcher + language selector + notifications + account menu */}
        <div className="flex items-center gap-1 lg:gap-2">
          <AccountSwitcher />

          <div data-tour="header-language">
            <LanguageSelector
              languages={languages}
              currentLanguage={getCurrentLanguage()}
              onLanguageChange={handleLanguageChange}
              tooltipText={t('toolbar.changeLanguage')}
            />
          </div>

          <div data-tour="header-notifications">
            <NotificationPanel
              notifications={mapNotifications(notifications)}
              unreadCount={unreadCount}
              onMarkAsRead={markAsRead}
              onMarkAllAsRead={markAllAsRead}
            />
          </div>

          <div data-tour="header-account">
            <AccountMenu
              user={user}
              onSignOut={handleLogout}
              isLoading={logout.isPending}
              menuItems={[
                // PENDIENTES DE EXPEDIENTE
                ...pendingOnboardingItems,
                // MI CUENTA
                {
                  key: 'profile',
                  label: t('toolbar.profile'),
                  icon: <UserIcon className="h-4 w-4" />,
                  href: '/settings?section=profile',
                  isActive: activeSection === 'profile',
                  separator: pendingOnboardingItems.length > 0,
                },
                // PREFERENCIAS
                {
                  key: 'notifications',
                  label: t('toolbar.notifications'),
                  icon: <BellIcon className="h-4 w-4" />,
                  href: '/settings?section=notifications',
                  separator: true,
                  isActive: activeSection === 'notifications',
                },
                {
                  key: 'preferences',
                  label: t('toolbar.preferences'),
                  icon: <PaletteIcon className="h-4 w-4" />,
                  href: '/settings?section=preferences',
                  isActive: activeSection === 'preferences' || activeSection === 'appearance',
                },
                {
                  key: 'limits',
                  label: t('toolbar.limits'),
                  icon: <BanknoteIcon className="h-4 w-4" />,
                  href: '/settings?section=transaction-limits',
                  isActive: activeSection === 'transaction-limits',
                },
                {
                  key: 'accessibility',
                  label: t('toolbar.accessibility'),
                  icon: <EyeIcon className="h-4 w-4" />,
                  href: '/settings?section=accessibility',
                  isActive: activeSection === 'accessibility',
                },
                // SEGURIDAD
                {
                  key: 'security',
                  label: t('toolbar.security'),
                  icon: <ShieldIcon className="h-4 w-4" />,
                  href: '/settings?section=security',
                  separator: true,
                  isActive: activeSection === 'security',
                },
                {
                  key: 'privacy',
                  label: t('toolbar.privacy'),
                  icon: <LockIcon className="h-4 w-4" />,
                  href: '/settings?section=privacy',
                  isActive: activeSection === 'privacy',
                },
                // AYUDA
                {
                  key: 'help',
                  label: t('toolbar.help'),
                  icon: <QuestionMarkCircleIcon className="h-4 w-4" />,
                  href: '/settings?section=help',
                  separator: true,
                  isActive: activeSection === 'help',
                },
              ]}
              renderLink={({ href, children, className, onClick }) => (
                <Link to={href} className={className} onClick={onClick}>
                  {children}
                </Link>
              )}
            />
          </div>
        </div>
      </div>

      <SessionWarning
        isOpen={showWarning}
        remainingTime={remainingTime}
        onExtend={onExtend}
        onLogout={handleLogout}
      />
    </>
  );
}
